import React, { useState } from 'react';
import './Solver.css';

interface Feature {
  icon: string;
  color: string;
  title: string;
  desc: string;
  saving: string;
}

interface Panel {
  id: string;
  label: string;
  storyHeader: string;
  scenario: string;
  aiBubble: string;
  features: Feature[];
}

const panels: Panel[] = [
  {
    id: 'sales',
    label: 'Sales & Follow-up',
    storyHeader: "What's happening in your sales pipeline right now",
    scenario: '"I send out quotes and then forget to chase them. Half my leads go cold because I\'m too busy to follow up. I know I\'m losing business but I just don\'t have time to fix it."',
    aiBubble: 'You have <strong>9 quotes sent in the last 30 days with no follow-up</strong>. Based on your average job value, that\'s roughly <strong>\u20AC18,000 sitting in your pipeline doing nothing</strong>. We set up automatic follow-ups \u2014 friendly, personalised, timed perfectly \u2014 so every lead gets chased without you lifting a finger.',
    features: [
      { icon: '\u2713', color: 'green', title: 'Automated Quote Follow-ups', desc: 'Every quote gets a personalised follow-up sequence at day 3, day 7, day 14. Written in your voice. Sent automatically.', saving: 'Clients see 25\u201340% more quotes converted' },
      { icon: '\u2605', color: 'amber', title: 'Lead Prioritisation', desc: 'Your leads are ranked from hottest to coldest based on behaviour \u2014 so when you pick up the phone, you call the right people first.', saving: 'Spend your sales time where it counts' },
      { icon: '\u21BB', color: 'blue', title: 'Repeat Customer Nudges', desc: 'When a regular customer hasn\'t been in touch for a while, a natural, personal check-in goes out to bring them back.', saving: 'Increases repeat business by 20\u201330%' },
      { icon: '\u26A1', color: 'purple', title: 'Instant New Lead Response', desc: 'When an enquiry comes in \u2014 through your website, email, or social \u2014 a response goes out within 2 minutes, even at midnight.', saving: 'First to respond wins. You\'ll always be first.' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin & Emails',
    storyHeader: 'Where your week actually goes',
    scenario: '"By the time I\'ve dealt with emails, processed things, chased invoices and updated everything, the actual work day is gone. I\'m working evenings just to keep up."',
    aiBubble: 'Looking at your last month: <strong>you spent roughly 6 hours on email alone</strong>. I can handle triage and draft replies for everything routine, leaving you only the things that genuinely need you. Combined with automated invoicing, <strong>most business owners get back 10\u201312 hours per week</strong>.',
    features: [
      { icon: '\u2709', color: 'green', title: 'Email Triage & Smart Drafts', desc: 'Your inbox is read, urgent items flagged, and routine replies drafted. You review and send in a fraction of the time.', saving: 'Cut email time by 60\u201370%' },
      { icon: '\u00A3', color: 'amber', title: 'Invoice & Payment Chasing', desc: 'Invoices go out automatically when a job is done. Overdue reminders sent at the right time, in the right tone.', saving: 'Get paid 40% faster. Zero awkwardness.' },
      { icon: '\u2630', color: 'blue', title: 'Booking & Scheduling', desc: 'Appointments, confirmations, reminders \u2014 all handled automatically. No more back-and-forth emails.', saving: 'Saves 3\u20134 hours per week on scheduling alone' },
      { icon: '\u2B1A', color: 'purple', title: 'Document Automation', desc: 'Contracts, job sheets, reports \u2014 generated from information you already have. No copy-pasting.', saving: 'Eliminates hours of weekly data entry' },
    ],
  },
  {
    id: 'customers',
    label: 'Customer Service',
    storyHeader: 'Your customer service right now',
    scenario: '"I\'m constantly interrupted by the same questions \u2014 prices, availability, opening times, how long things take. I love my customers but I can\'t get anything done."',
    aiBubble: 'You\'re answering the <strong>same 8 questions roughly 40 times a week</strong> \u2014 that\'s about 5 hours. A well-trained system handles them instantly. <strong>Customers get instant answers 24/7</strong>, you only get pulled in for conversations that genuinely need you.',
    features: [
      { icon: '\u2328', color: 'green', title: 'Website Chat', desc: 'Answers questions, takes bookings, qualifies leads \u2014 24/7. Sounds like you, not a robot. Hands over when it needs to.', saving: 'Saves 5\u20138 hours per week on customer queries' },
      { icon: '\u260E', color: 'amber', title: 'WhatsApp & Messenger Automation', desc: 'Auto-replies to common questions on WhatsApp, Facebook, and Instagram. Customers get an answer in seconds.', saving: 'Never miss an enquiry again' },
      { icon: '\u2606', color: 'blue', title: 'Automatic Review Requests', desc: 'Every happy customer gets asked for a review at exactly the right moment. More reviews, more trust, more sales.', saving: '3x more reviews with zero effort' },
      { icon: '\u23F0', color: 'purple', title: 'Appointment Reminders', desc: 'Automatic reminders sent by text or email before every appointment. Fewer no-shows.', saving: 'Reduces no-shows by up to 60%' },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting & Numbers',
    storyHeader: 'Your numbers right now',
    scenario: '"I don\'t really know what my margins are. I know roughly what I\'m turning over, but I have no idea which services or customers are actually profitable."',
    aiBubble: 'Here\'s something interesting: <strong>your busiest service is often not your most profitable one</strong>. A live dashboard shows revenue today, this week, this month. Profit by service. Best customers. <strong>No spreadsheets. No waiting for your accountant.</strong>',
    features: [
      { icon: '\u2261', color: 'green', title: 'Live Business Dashboard', desc: 'Revenue, margin, outstanding invoices, best customers \u2014 on your phone, updated every hour, in plain English.', saving: 'Know your numbers without being an accountant' },
      { icon: '\u00A3', color: 'amber', title: 'Profit by Service or Product', desc: 'See exactly what makes you money and what just looks like it does. Most businesses find one thing to cut immediately.', saving: 'Identifies hidden profit in weeks, not years' },
      { icon: '\u2299', color: 'blue', title: 'Sales vs Target Tracking', desc: 'Set your monthly target once. You\'re told daily whether you\'re on track \u2014 and what to do this week to hit it.', saving: 'No more year-end surprises' },
      { icon: '\u2197', color: 'purple', title: 'Weekly Summary', desc: 'Every Monday morning: what happened last week, what to focus on this week, any warnings, any opportunities.', saving: '5 minutes to know everything. Every week.' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing & Content',
    storyHeader: 'Your marketing right now',
    scenario: '"I know I should be posting on social media, sending emails to customers, keeping my website up to date. But by the time I\'ve done everything else, there\'s just no time."',
    aiBubble: 'The good news: <strong>marketing is one of the easiest things to automate</strong>. A content system drafts your social posts, emails, and website updates based on what\'s happening in your business. You spend 20 minutes approving content on a Monday, and it goes out <strong>every day for the rest of the week</strong>.',
    features: [
      { icon: '\u2709', color: 'green', title: 'Social Media \u2014 Done For You', desc: 'Posts drafted in your voice, based on your business. You approve with one tap on Monday. They go out all week.', saving: 'Consistent presence without the daily effort' },
      { icon: '\u2709', color: 'amber', title: 'Customer Email Campaigns', desc: 'Regular emails to your customer list \u2014 offers, updates, seasonal content \u2014 written, approved by you, sent automatically.', saving: 'Stay top of mind. Drive repeat business.' },
      { icon: '\u2315', color: 'blue', title: 'Google Presence Optimisation', desc: 'Your Google Business profile stays updated, reviews get responses, and you show up when local customers search.', saving: 'More local visibility. More walk-in enquiries.' },
      { icon: '\u270E', color: 'purple', title: 'Website Copy That Converts', desc: 'Website content rewritten so it answers what customers search for \u2014 and convinces them to get in touch.', saving: 'More enquiries from the same website traffic' },
    ],
  },
];

const Solver: React.FC = () => {
  const [activeTab, setActiveTab] = useState('sales');

  return (
    <section className="solver" id="solver">
      <div className="solver-inner">
        <div className="solver-head reveal">
          <span className="eyebrow">Pick your biggest headache</span>
          <h2>Show me how this fixes <em>my problem</em>.</h2>
          <p>Click whichever problem feels most urgent right now and we'll show you exactly what changes.</p>
        </div>
        <div className="solver-tabs reveal">
          {panels.map((panel) => (
            <button
              key={panel.id}
              className={`solver-tab ${activeTab === panel.id ? 'active' : ''}`}
              onClick={() => setActiveTab(panel.id)}
            >
              {panel.label}
            </button>
          ))}
        </div>
        {panels.map((panel) => (
          <div key={panel.id} className={`solver-panel ${activeTab === panel.id ? 'active' : ''}`}>
            <div className="panel-story">
              <div className="panel-story-header">{panel.storyHeader}</div>
              <div className="panel-story-body">
                <div className="story-scenario">{panel.scenario}</div>
                <div className="story-ai">
                  <div className="ai-dot">SMB</div>
                  <div className="ai-bubble" dangerouslySetInnerHTML={{ __html: panel.aiBubble }} />
                </div>
              </div>
            </div>
            <div className="panel-features">
              {panel.features.map((feat, j) => (
                <div className="feat" key={j}>
                  <div className={`feat-icon ${feat.color}`}>{feat.icon}</div>
                  <div>
                    <div className="feat-title">{feat.title}</div>
                    <div className="feat-desc">{feat.desc}</div>
                    <span className="feat-saving">{feat.saving}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Solver;
